import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid2,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { Add, ArrowBack, Close, CloudSync, Videocam } from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { enqueueSnackbar } from 'notistack';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import moment from 'moment';

import Wrapper from '../components/Wrapper';
import PageLoader from '../components/PageLoader';
import Editor from '../components/Editor';
import { addEvent, getEvent, getEventIntegrationStatus, syncEvent, updateEvent } from '../services/events';

const getApiMessage = (error, fallback) => (
  error?.response?.data?.message || error?.message || fallback
);

const EventForm = () => {
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState('Spiritual Tara');
  const [summary, setSummary] = useState('');
  const [eventDetails, setEventDetails] = useState('');
  const [eventDate, setEventDate] = useState(null);
  const [eventTime, setEventTime] = useState(null);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [onlineOffline, setOnlineOffline] = useState('Online');
  const [venue, setVenue] = useState('');
  const [facilitator, setFacilitator] = useState('');
  const [amount, setAmount] = useState(0);
  const [landingUrl, setLandingUrl] = useState('');
  const [tagValue, setTagValue] = useState('');
  const [tags, setTags] = useState([]);
  const [createZoomMeeting, setCreateZoomMeeting] = useState(true);
  const [syncToSangam, setSyncToSangam] = useState(true);
  const [integrationStatus, setIntegrationStatus] = useState('');
  const [integrationError, setIntegrationError] = useState('');
  const [zoomMeetingId, setZoomMeetingId] = useState('');
  const [sangamEventId, setSangamEventId] = useState('');
  const [zoomConfigured, setZoomConfigured] = useState(null);
  const [sangamConfigured, setSangamConfigured] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { id: eventId } = useParams();
  const navigate = useNavigate();

  const getData = async () => {
    setIsLoading(true);
    const { data, error } = await getEvent({ id: eventId });
    setIsLoading(false);

    if (!data?.success) {
      enqueueSnackbar(getApiMessage(error, 'Unable to load event'), { variant: 'error' });
      return;
    }

    const event = data.data;
    setTitle(event.title || '');
    setEventType(event.type || 'Spiritual Tara');
    setSummary(event.summary || '');
    setEventDetails(event.details || '');
    setTags(event.tags || []);
    setDurationMinutes(event.duration_minutes || 60);
    setOnlineOffline(event.online_offline || 'Online');
    setVenue(event.venue || '');
    setFacilitator(event.facilitator || '');
    setAmount(event.amount || 0);
    setLandingUrl(event.landing_url?.startsWith('/event/') ? '' : (event.landing_url || ''));
    setCreateZoomMeeting(Boolean(event.create_zoom_meeting || event.zoom_meeting_id));
    setSyncToSangam(event.sync_to_sangam !== false);
    setIntegrationStatus(event.integration_status || '');
    setIntegrationError(event.integration_error || '');
    setZoomMeetingId(event.zoom_meeting_id || '');
    setSangamEventId(event.sangam_event_id || '');

    if (event.event_date && moment(event.event_date, 'DD-MM-YYYY', true).isValid()) {
      setEventDate(moment(event.event_date, 'DD-MM-YYYY'));
    }
    if (event.event_time && moment(event.event_time, 'HH:mm', true).isValid()) {
      setEventTime(moment(event.event_time, 'HH:mm'));
    }
  };

  useEffect(() => {
    getEventIntegrationStatus().then(({ data }) => {
      if (!data?.success) return;
      setZoomConfigured(data.zoom_configured);
      setSangamConfigured(data.sangam_configured);
    });
    if (eventId) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleModeChange = (value) => {
    setOnlineOffline(value);
    if (value === 'Offline') setCreateZoomMeeting(false);
    if (value === 'Online' && !eventId) setCreateZoomMeeting(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setIntegrationError('');

    const payload = {
      title,
      summary,
      details: eventDetails,
      type: eventType,
      tags,
      event_date: moment(eventDate).format('DD-MM-YYYY'),
      event_time: eventTime ? moment(eventTime).format('HH:mm') : '',
      duration_minutes: Number(durationMinutes),
      online_offline: onlineOffline,
      venue,
      facilitator,
      amount: Number(amount),
      landing_url: landingUrl,
      registration_link: landingUrl,
      create_zoom_meeting: onlineOffline === 'Online' && createZoomMeeting,
      sync_to_sangam: syncToSangam
    };

    const result = eventId
      ? await updateEvent({ data: payload, id: eventId })
      : await addEvent(payload);

    setIsSaving(false);
    const responseData = result.data || result.error?.response?.data;

    if (responseData?.success) {
      setIntegrationStatus(responseData.integration_status || 'synced');
      setZoomMeetingId(responseData.zoom_meeting_id || zoomMeetingId);
      setSangamEventId(responseData.sangam_event_id || sangamEventId);
      enqueueSnackbar('Event and integrations saved', { variant: 'success' });
      if (!eventId && responseData.event_id) {
        navigate(`/edit-event/${responseData.event_id}`, { replace: true });
      }
      return;
    }

    const failedEventId = responseData?.event_id;
    const message = getApiMessage(result.error, responseData?.message || 'Unable to save linked event');
    setIntegrationStatus(responseData?.integration_status || 'failed');
    setIntegrationError(message);
    enqueueSnackbar(message, { variant: 'error' });
    if (!eventId && failedEventId) {
      navigate(`/edit-event/${failedEventId}`, { replace: true });
    }
  };

  const handleRetrySync = async () => {
    setIsSyncing(true);
    const { data, error } = await syncEvent({ id: eventId });
    setIsSyncing(false);

    if (data?.success) {
      enqueueSnackbar('Event integrations synchronized', { variant: 'success' });
      getData();
      return;
    }

    const message = getApiMessage(error, 'Unable to synchronize event');
    setIntegrationError(message);
    enqueueSnackbar(message, { variant: 'error' });
  };

  const handleAddTag = () => {
    const value = tagValue.trim();
    if (value && !tags.includes(value)) setTags([...tags, value]);
    setTagValue('');
  };

  const isInvalid = (
    !title.trim() ||
    !summary.trim() ||
    !eventDetails ||
    !eventDate ||
    (onlineOffline === 'Online' && createZoomMeeting && !eventTime) ||
    (onlineOffline === 'Online' && createZoomMeeting && zoomConfigured !== true) ||
    (syncToSangam && sangamConfigured !== true) ||
    (onlineOffline === 'Offline' && !venue.trim())
  );

  return (
    <Wrapper>
      <PageLoader show={isLoading} />

      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Link to="/events" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ArrowBack fontSize="small" />
          <Typography variant="body2" color="text.secondary">Events</Typography>
        </Link>
        <Typography variant="body2" color="text.secondary">/</Typography>
        <Typography variant="body2" fontWeight={600}>{eventId ? 'Edit Event' : 'New Event'}</Typography>
      </Box>

      {integrationStatus && (
        <Alert
          severity={integrationStatus === 'synced' ? 'success' : integrationStatus === 'not_requested' ? 'info' : 'warning'}
          action={(integrationStatus === 'failed' || integrationStatus === 'partial') && eventId ? (
            <Button color="inherit" size="small" onClick={handleRetrySync} disabled={isSyncing} startIcon={<CloudSync />}>
              {isSyncing ? 'Syncing' : 'Retry'}
            </Button>
          ) : null}
          sx={{ mb: 2 }}
        >
          Integration status: {integrationStatus.replace('_', ' ')}
          {integrationError ? ` - ${integrationError}` : ''}
        </Alert>
      )}

      {onlineOffline === 'Online' && createZoomMeeting && zoomConfigured === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>Zoom connection required</Alert>
      )}
      {syncToSangam && sangamConfigured === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>Sangam CRM connection required</Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <TextField fullWidth required label="Title" value={title} onChange={(event) => setTitle(event.target.value)} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <TextField select fullWidth label="Listing" value={eventType} onChange={(event) => setEventType(event.target.value)}>
              <MenuItem value="Spiritual Tara">Spiritual Tara</MenuItem>
              <MenuItem value="Other">Community</MenuItem>
            </TextField>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }}>
            <DatePicker
              label="Date"
              format="DD-MM-YYYY"
              value={eventDate}
              onChange={setEventDate}
              slotProps={{ textField: { fullWidth: true, required: true } }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <TimePicker
              label="Start time"
              value={eventTime}
              onChange={setEventTime}
              slotProps={{ textField: { fullWidth: true, required: onlineOffline === 'Online' && createZoomMeeting } }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              type="number"
              label="Duration"
              value={durationMinutes}
              onChange={(event) => setDurationMinutes(event.target.value)}
              slotProps={{ input: { endAdornment: <InputAdornment position="end">minutes</InputAdornment> } }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <TextField select fullWidth label="Format" value={onlineOffline} onChange={(event) => handleModeChange(event.target.value)}>
              <MenuItem value="Online">Online</MenuItem>
              <MenuItem value="Offline">Offline</MenuItem>
            </TextField>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Facilitator" value={facilitator} onChange={(event) => setFacilitator(event.target.value)} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Price"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              slotProps={{ input: { startAdornment: <InputAdornment position="start">Rs</InputAdornment> } }}
            />
          </Grid2>

          {onlineOffline === 'Offline' && (
            <Grid2 size={12}>
              <TextField fullWidth required label="Venue" value={venue} onChange={(event) => setVenue(event.target.value)} />
            </Grid2>
          )}

          <Grid2 size={12}>
            <TextField fullWidth label="Landing page path" placeholder="/workshop/event-name" value={landingUrl} onChange={(event) => setLandingUrl(event.target.value)} />
          </Grid2>

          <Grid2 size={12}>
            <Divider />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, pt: 2 }}>
              <FormControlLabel
                control={<Switch checked={syncToSangam} onChange={(event) => setSyncToSangam(event.target.checked)} />}
                label="Sangam CRM"
              />
              <FormControlLabel
                control={<Switch checked={createZoomMeeting} onChange={(event) => setCreateZoomMeeting(event.target.checked)} disabled={onlineOffline !== 'Online'} />}
                label="Create Zoom meeting"
              />
              {zoomMeetingId && <Chip icon={<Videocam />} label={`Zoom ${zoomMeetingId}`} variant="outlined" />}
              {sangamEventId && <Chip icon={<CloudSync />} label="Sangam linked" variant="outlined" />}
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField multiline fullWidth required label="Summary" value={summary} onChange={(event) => setSummary(event.target.value)} rows={6} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Tags</Typography>
            <TextField
              fullWidth
              label="Tag"
              value={tagValue}
              onChange={(event) => setTagValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddTag();
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleAddTag} disabled={!tagValue.trim()}><Add /></IconButton>
                    </InputAdornment>
                  )
                }
              }}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, minHeight: 40 }}>
              {tags.map((tag, index) => (
                <Chip key={`${tag}-${index}`} label={tag} onDelete={() => setTags(tags.filter((_, itemIndex) => itemIndex !== index))} deleteIcon={<Close />} />
              ))}
            </Box>
          </Grid2>

          <Grid2 size={12}>
            <Editor value={eventDetails} onChange={setEventDetails} />
          </Grid2>

          <Grid2 size={12} display="flex" justifyContent="flex-end">
            <Button variant="contained" disabled={isSaving || isInvalid} onClick={handleSave} startIcon={!isSaving ? <CloudSync /> : null}>
              {isSaving ? <CircularProgress color="inherit" size={20} /> : 'Save and link'}
            </Button>
          </Grid2>
        </Grid2>
      </Paper>
    </Wrapper>
  );
};

export default EventForm;
